import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './icons';

export interface Command {
  id: string;
  label: string;
  hint?: string;
  icon: string;
  run: () => void;
}

/** Палитра команд (Ctrl+K / ⌘K): переход к разделу, открытие объявления по номеру, смена темы. */
export function CommandPalette({ commands, onClose, onListing }: { commands: Command[]; onClose: () => void; onListing: (id: string) => void }) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.focus(), []);

  const items = useMemo(() => {
    const s = q.trim().toLowerCase();
    const id = /^#?(\d{1,19})$/.exec(s)?.[1];
    const found = commands.filter((c) => !s || c.label.toLowerCase().includes(s) || (c.hint ?? '').toLowerCase().includes(s));
    return id ? [{ id: `listing-${id}`, label: `Открыть объявление #${id}`, hint: 'модерация', icon: 'list', run: () => onListing(id) }, ...found] : found;
  }, [q, commands, onListing]);
  useEffect(() => setSel(0), [q]);

  const choose = (c: Command | undefined) => {
    if (!c) return;
    onClose();
    c.run();
  };

  return (
    <div className="cmdk-backdrop" onClick={onClose}>
      <div className="cmdk" role="dialog" aria-modal="true" aria-label="Быстрый переход" onClick={(e) => e.stopPropagation()}>
        <div className="cmdk-input">
          <Icon name="search" />
          <input
            ref={input}
            value={q}
            placeholder="Раздел, действие или номер объявления…"
            aria-label="Быстрый переход"
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-list"
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setSel((i) => Math.min(i + 1, items.length - 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((i) => Math.max(i - 1, 0)); }
              else if (e.key === 'Enter') { e.preventDefault(); choose(items[sel]); }
              else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
            }}
          />
        </div>
        <div className="cmdk-list" id="cmdk-list" role="listbox" aria-label="Результаты">
          {items.map((c, i) => (
            <button key={c.id} type="button" role="option" aria-selected={i === sel} className="cmdk-item" onMouseEnter={() => setSel(i)} onClick={() => choose(c)}>
              <Icon name={c.icon} />
              {c.label}
              {c.hint ? <small>{c.hint}</small> : null}
            </button>
          ))}
          {!items.length ? <div className="muted pad" style={{ padding: 14 }}>Ничего не найдено</div> : null}
        </div>
        <div className="cmdk-foot">
          <span><kbd>↑</kbd> <kbd>↓</kbd> выбор</span>
          <span><kbd>Enter</kbd> открыть</span>
          <span><kbd>Esc</kbd> закрыть</span>
          <span>#123 — объявление по номеру</span>
        </div>
      </div>
    </div>
  );
}
